import { observer } from "mobx-react-lite"
import { Link } from "react-router-dom"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderMinimal,
  TableRow,
} from "@/components/table"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { FeesIcon } from "@/domains/icons"
import { dollarsPrettified } from "@/domains/library"
import { ClaimFeesButton as ClaimV1FeesButton } from "@/domains/thick-v1/components/my-positions/claim-manual-cl-fees-button"
import type { ManualClPositionStore as ManualClV1PositionStore } from "@/domains/thick-v1/positions"
import { ClaimFeesButton as ClaimV2FeesButton } from "@/domains/thick-v2/components/my-positions/claim-manual-cl-fees-button"
import type { ManualClPositionStore as ManualClV2PositionStore } from "@/domains/thick-v2/positions"
import { DisplayTokens } from "@/domains/tokens/components/display-tokens"

type ManualClEarnedFeesRow =
  | { version: "V1"; position: ManualClV1PositionStore }
  | { version: "V2"; position: ManualClV2PositionStore }

export const ManualClEarnedFees = observer(() => {
  const scaleApp = useScaleApplication()
  const rows: ManualClEarnedFeesRow[] = [
    ...scaleApp.ManualClV1.Positions.activePositions.map((position) => ({ version: "V1" as const, position })),
    ...scaleApp.ManualClV2.Positions.activePositions.map((position) => ({ version: "V2" as const, position })),
  ].filter(({ position }) => !position.isBurned && (position.claimableFeesUsd > 0 || position.earnedFeesUsd > 0))

  const hasFees = rows.length > 0

  return (
    <Card.Container className="flex w-full flex-col gap-6 rounded-lg border border-p400-light bg-p400 p-4 lg:p-6">
      <div className="flex flex-row items-center justify-between gap-2">
        <div>
          <Card.HeaderTitle>Fees</Card.HeaderTitle>
          <Card.HeaderDescription>
            Fees are earned by managing{" "}
            <Link to="/liquidity/cl/v1" className="font-normal">
              manual CL positions
            </Link>{" "}
            in your wallet.
          </Card.HeaderDescription>
        </div>
      </div>

      {hasFees ? (
        <div className="overflow-hidden rounded-t-xl border border-p400-light">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderMinimal className="pl-4 text-left" isSortDisabled>
                  Pool
                </TableHeaderMinimal>
                <TableHeaderMinimal className="text-center" isSortDisabled>
                  Version
                </TableHeaderMinimal>
                <TableHeaderMinimal className="text-right" isSortDisabled>
                  Position
                </TableHeaderMinimal>
                <TableHeaderMinimal className="text-right" isSortDisabled>
                  Claimable
                </TableHeaderMinimal>
                <TableHeaderMinimal className="text-right" isSortDisabled>
                  Lifetime Earned
                </TableHeaderMinimal>
                <TableHeaderMinimal className="text-center" isSortDisabled>
                  Claim
                </TableHeaderMinimal>
                <TableHeaderMinimal className="text-center" isSortDisabled>
                  Manage
                </TableHeaderMinimal>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <ManualClEarnedFeesTableRow key={`${row.version}-${row.position.positionId}`} row={row} />
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <NoManualClFeesView />
      )}
    </Card.Container>
  )
})

const NoManualClFeesView = () => {
  return (
    <div className="flex flex-col items-center justify-center gap-2 bg-p300 p-4">
      <Card.HeaderIconContainer>
        <FeesIcon className="h-4 w-4 text-t200" />
      </Card.HeaderIconContainer>
      <div className="flex flex-col items-center justify-center">
        <Card.HeaderTitle>Fees</Card.HeaderTitle>
        <div className="text-center text-xs text-t200">
          Fees are earned when you provide liquidity in manual concentrated liquidity pools.
        </div>
      </div>
      <Button variant="percentage" asChild>
        <Link to="/liquidity/cl/v1">Start Earning</Link>
      </Button>
    </div>
  )
}

const ManualClEarnedFeesTableRow = observer(({ row }: { row: ManualClEarnedFeesRow }) => {
  const position = row.position
  const pool = position.pool
  const token0 = pool.token0
  const token1 = pool.token1
  const claimableFeesToken0 = position.pendingFees0 === undefined ? 0 : Number(token0.formatUnits(position.pendingFees0))
  const claimableFeesToken1 = position.pendingFees1 === undefined ? 0 : Number(token1.formatUnits(position.pendingFees1))
  const manageHref = `/liquidity/cl/${row.version.toLowerCase()}/${pool.address.raw}/manage/${position.tokenId.toString()}`

  return (
    <TableRow className="border-t border-t-p400-light">
      <TableCell className="pl-4">
        <div className="flex items-center gap-2">
          <DisplayTokens tokens={[token0, token1]} tokenSize={28} />
          <div>
            <div className="whitespace-nowrap text-t100">{pool.displayName}</div>
            <div className="text-xs text-t300">{position.isInRange ? "In range" : "Out of range"}</div>
          </div>
        </div>
      </TableCell>
      <TableCell className="text-center">
        <span className="rounded-full border border-p400-light bg-p300 px-2 py-1 text-xs font-semibold text-t200">
          {row.version}
        </span>
      </TableCell>
      <TableCell className="text-right text-t100">
        {position.nfpmTokenId === undefined ? `ref #${position.tokenId.toString()}` : `#${position.nfpmTokenId.toString()}`}
      </TableCell>
      <TableCell className="text-right">
        <div className="text-t100">{dollarsPrettified(position.claimableFeesUsd)}</div>
        <div className="text-xs text-t300">
          {claimableFeesToken0.toLocaleString(undefined, { maximumFractionDigits: 4 })} {token0.symbol}
        </div>
        <div className="text-xs text-t300">
          {claimableFeesToken1.toLocaleString(undefined, { maximumFractionDigits: 4 })} {token1.symbol}
        </div>
      </TableCell>
      <TableCell className="text-right text-t100">{dollarsPrettified(position.earnedFeesUsd)}</TableCell>
      <TableCell className="text-center">
        {row.version === "V1" ? (
          <ClaimV1FeesButton position={row.position} disabled={row.position.isBurned || row.position.claimableFeesUsd === 0} />
        ) : (
          <ClaimV2FeesButton position={row.position} disabled={row.position.isBurned || row.position.claimableFeesUsd === 0} />
        )}
      </TableCell>
      <TableCell className="text-center">
        <Button variant="neutral" asChild size="md" className="mx-auto w-fit">
          <Link to={manageHref}>Manage</Link>
        </Button>
      </TableCell>
    </TableRow>
  )
})
